import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { RULES_PATH } from '../rules/rulePaths'
import { PremiumGate } from '../../platform/access/PremiumGate'
import type { LocalDate } from '../../platform/localDate'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import type { CategoryMap } from '../cashflow/cashflowCategories'
import type { ReportQuery } from '../cashflow/cashflowWire'
import type { ReportQueryRequest } from '../cashflow/useCashflow'
import { UnconvertedNote } from '../cashflow/UnconvertedNote'
import { ExportButton } from './ExportButton'
import { ReportChart } from './ReportChart'
import { ReportPreview } from './ReportPreview'
import { ReportRail } from './ReportRail'
import type { FilterOptions } from '../transactions/useFilterOptions'
import { activePreset, filterFieldCount, subtitle, TAX_DEDUCTIBLE_TAG, type ChartType, type FilterNames, type PresetId, type ReportSpec } from './reportSpec'
import { ReportTable } from './ReportTable'
import { dimensionNoun, drillPath, queryAxis, queryChartSeries, queryRows, sameAxis, TAGS_OVERLAP_NOTE, tableNote } from './reportView'
import './reports.css'

type Query = { state: AsyncState<ReportQuery>; retry: () => void; fetching: boolean }

type Props = {
  spec: ReportSpec
  /** The table's request, which the PDF export repeats. */
  request: ReportQueryRequest
  today: LocalDate
  phone: boolean
  tablet: boolean
  /** `limit=100`: every row the server will list. */
  table: Query
  /** `limit=4`: the chart's top rows and the server's "Other". The table never reads it. */
  chart: Query
  filterNames: FilterNames
  names: CategoryMap
  groups: { id: string; name: string }[]
  groupsLoading?: boolean
  options: Pick<FilterOptions, 'accounts' | 'owners' | 'tags'> & Partial<Pick<FilterOptions, 'lists'>>
  onSpecChange: (next: ReportSpec, control: 'range' | 'columns' | 'filter' | 'rows' | 'preset') => void
  onPreset: (id: PresetId) => void
  onChartChange: (t: ChartType) => void
  onPickMerchant: () => void
}

const CHARTS = [
  { value: 'stacked', label: 'Stacked' },
  { value: 'grouped', label: 'Grouped' },
  { value: 'line', label: 'Line' },
] as const

/** The nav's Transactions entry (`shell/nav.ts`); no shared constant exists. */
const TRANSACTIONS_PATH = '/transactions'

const NOUN = {
  category: dimensionNoun('category', 1),
  group: dimensionNoun('group', 1),
  merchant: dimensionNoun('merchant', 1),
  owner: dimensionNoun('owner', 1),
  account: dimensionNoun('account', 1),
  tag: dimensionNoun('tag', 1),
} as const

// Overlapping tags make a stacked column taller than the Total (Q8), so it is not offered.
/** One string for the option's screen-reader reason and the visible note. */
const STACK_REASON = 'Tags overlap, so stacked bars would overstate the total.'
const CHARTS_NO_STACK = CHARTS.map((c) => (c.value === 'stacked' ? { ...c, unavailable: STACK_REASON } : c))

const KIND: Record<ChartType, string> = {
  stacked: 'Stacked bar chart',
  grouped: 'Grouped bar chart',
  line: 'Line chart',
}

function ResultSkeleton() {
  return (
    <Panel className="rp-panel" aria-hidden="true">
      <Skeleton height={14} width="30%" />
      <Skeleton height={200} />
      <Skeleton height={14} width="60%" />
      <Skeleton height={14} />
      <Skeleton height={14} />
    </Panel>
  )
}

function withRetry(s: AsyncState<ReportQuery>, retry: () => void): AsyncState<ReportQuery> {
  return s.status === 'error' ? { ...s, recovery: { label: 'Try again', onAct: retry } } : s
}

/**
 * `/reports`'s presentational screen. It fetches nothing; the container hands it the matrix. A fixed
 * layout (G1): the rail on the left and the result on the right at desktop and wide, the rail above the
 * result on a tablet, and on a phone no rail and a read-only list (R14, E2: screen size, not tier).
 *
 * The result sits inside the `cashflow_details` gate. A free user gets the locked picture and the
 * container fetches nothing (P10).
 */
export function ReportsScreen({
  spec,
  request,
  today,
  phone,
  tablet,
  table,
  chart: chartQuery,
  filterNames,
  names,
  groups,
  groupsLoading,
  options,
  onSpecChange,
  onPreset,
  onChartChange,
  onPickMerchant,
}: Props) {
  const text = subtitle(spec, filterNames)
  const deductible = activePreset(spec) === 'tax_deductible'
  const rail = (
    <ReportRail
      spec={spec}
      today={today}
      groups={groups}
      groupsLoading={groupsLoading}
      accounts={options.accounts}
      owners={options.owners}
      tags={options.tags}
      lists={options.lists}
      filterNames={filterNames}
      onChange={onSpecChange}
      onPreset={onPreset}
      onPickMerchant={onPickMerchant}
      incomeVsExpenseTo={spec.columns === 'quarter' ? '/cashflow?granularity=quarter' : '/cashflow'}
    />
  )
  const busy = (table.fetching && table.state.status === 'success') || (chartQuery.fetching && chartQuery.state.status === 'success')
  const layout = phone ? 'rp-layout rp-layout--phone' : tablet ? 'rp-layout rp-layout--tablet' : 'rp-layout'

  return (
    <div className="rp-page">
      <div className="rp-head">
        <p className="rp-head__line">{`Your spending by ${NOUN[spec.rows]}, ${spec.columns === 'owner' ? 'owner by owner' : 'period by period'}.`}</p>
        <ExportButton request={request} spec={spec} periods={table.state.status === 'success' ? table.state.data.periods.length : null} />
      </div>
      <div className={layout}>
        {!phone && rail}
        <div className="rp-result" aria-busy={busy ? 'true' : undefined}>
          {phone && (
            <>
              <p className="note">Change the range and columns on a larger screen.</p>
              <p className="rp-subtitle">{text}</p>
            </>
          )}
          <PremiumGate entitlement="cashflow_details" label="reports" panelKind="report" preview={<ReportPreview />}>
            <Async
              surface="reports"
              state={withRetry(table.state, table.retry)}
              skeleton={<ResultSkeleton />}
              empty={deductible ? { title: `No spending is tagged ${TAX_DEDUCTIBLE_TAG}`, body: text } : { title: 'No spending matches this report', body: text }}
            >
              {(t) => {
                const first = t.periods[0]?.start.slice(0, 4)
                const last = t.periods.at(-1)?.start.slice(0, 4)
                const axis = queryAxis(t, first !== last)
                const chartData = chartQuery.state.status === 'success' && sameAxis(t, chartQuery.state.data) ? chartQuery.state.data : null
                const rows = queryRows(t, chartData, names)
                const noun = NOUN[spec.rows]
                const dimension = noun.charAt(0).toUpperCase() + noun.slice(1)
                const title = `Spending by ${noun}${axis.kind === 'owner' ? ' and owner' : ''}`
                const type: ChartType = phone ? (spec.rows === 'tag' ? 'grouped' : 'stacked') : spec.chart
                const note = tableNote(t)
                return (
                  <div className="rp-stack">
                    <Panel className="rp-panel">
                      <div className="rp-panel__head">
                        <div>
                          <h2 className="rp-panel__title">{title}</h2>
                          {!phone && <p className="rp-subtitle">{text}</p>}
                        </div>
                        {!phone && (
                          <SegmentedControl
                            label="Chart type"
                            name="rp-chart-type"
                            options={spec.rows === 'tag' ? CHARTS_NO_STACK : CHARTS}
                            value={spec.chart}
                            onChange={onChartChange}
                            tone="accent"
                          />
                        )}
                      </div>
                      {spec.rows === 'tag' && !phone && (
                        <p className="note">
                          {STACK_REASON}
                        </p>
                      )}
                      {chartData !== null ? (
                        <ReportChart
                          series={queryChartSeries(chartData, names)}
                          labels={axis.labels}
                          current={axis.kind === 'period' ? axis.current : null}
                          type={type}
                          currency={t.currency}
                          label={`${KIND[type]} of ${noun === 'category' ? 'spending by category' : title.toLowerCase()}, ${axis.labels[0] ?? ''} to ${axis.labels.at(-1) ?? ''}`}
                        />
                      ) : chartQuery.state.status === 'error' ? (
                        <p className="note" role="status">
                          The chart couldn&apos;t be loaded. The table below is complete.
                        </p>
                      ) : (
                        <Skeleton height={200} />
                      )}
                    </Panel>
                    <UnconvertedNote unconverted={t.unconverted} />
                    <Panel className="rp-panel">
                      {t.tagsOverlap === true && <p className="note">{TAGS_OVERLAP_NOTE}</p>}
                      <ReportTable
                        rows={rows}
                        axis={axis}
                        dimension={dimension}
                        title={title}
                        currency={t.currency}
                        phone={phone}
                        rowHref={(r) => (filterFieldCount(spec.filters) === 0 ? drillPath(t.rowsBy, r, spec.columns, spec.end, today) : null)}
                      />
                      {note !== null && <p className="note">{note}</p>}
                    </Panel>
                  </div>
                )
              }}
            </Async>
            {deductible && table.state.status === 'empty' && (
              <div className="rp-hint">
                <p className="note">
                  Tag a transaction {TAX_DEDUCTIBLE_TAG}, or create a rule that adds the tag to everything in a category.
                </p>
                <p className="rp-hint__links">
                  <InlineLink to={RULES_PATH}>Auto-rules</InlineLink>
                  <InlineLink to={TRANSACTIONS_PATH}>Transactions</InlineLink>
                </p>
              </div>
            )}
          </PremiumGate>
        </div>
      </div>
    </div>
  )
}
